import { registerIndicator, type IndicatorDrawParams, type KLineData } from 'klinecharts'
import { registerOnce } from './registrationUtils'
/**
 * Session classification comes from `sessionFilter`, which is also what
 * `isRegularSession` (and therefore DataLayer's extended-hours filter) reads.
 *
 * This module used to carry its own copy with the boundaries hardcoded at
 * 04:00 / 09:30 / 16:00 / 20:00. That copy was wrong on the ~3 NYSE half days a
 * year, where the regular session ends at 13:00 and after-hours at 17:00 — so
 * the shading claimed 13:00–16:00 was still regular while the data layer had
 * already filtered those bars out as extended. Sharing one classifier is what
 * keeps the highlight and the filter from disagreeing.
 */
import { classifySessionSegment } from './sessionFilter'

export interface SessionHighlightConfig {
  preMarketColor: string
  afterHoursColor: string
  timezone: string
}

const INDICATOR_NAME = 'SESSION_HIGHLIGHT'

/** The narrow canvas surface this draw needs — fake-ctx testable. */
export type SessionHighlightCtx = {
  fillStyle: string | CanvasGradient | CanvasPattern
  save: () => void
  restore: () => void
  fillRect: (x: number, y: number, w: number, h: number) => void
}

export interface SessionHighlightDrawParams {
  ctx: SessionHighlightCtx
  kLineDataList: Array<{ timestamp: number } | undefined>
  visibleRange: { from: number; to: number }
  bounding: { height: number }
  barSpace: { halfBar: number }
  xAxis: { convertToPixel: (dataIndex: number) => number }
  indicator: { extendData?: unknown }
}

/**
 * Shade the pre-market and after-hours stretches behind the bars.
 *
 * Exported rather than inlined into the registration (the `drawTpo` / `drawHtf`
 * precedent) so the session classification can be asserted without a chart —
 * this file previously had no tests at all, which is how its forked copy of the
 * session boundaries went unnoticed.
 */
export function drawSessionHighlight(params: SessionHighlightDrawParams): boolean {
  const { ctx, kLineDataList, visibleRange, bounding, barSpace, xAxis, indicator } = params
  const config = indicator.extendData as SessionHighlightConfig | undefined
  if (!config) return false

  const tz = config.timezone || 'America/New_York'
  const { from, to } = visibleRange

  ctx.save()

  // Walk through visible bars and batch consecutive same-session bars
  let batchStart = -1
  let batchSession: 'pre' | 'after' | null = null

  const drawBatch = (startIdx: number, endIdx: number, color: string) => {
    const x1 = xAxis.convertToPixel(startIdx) - barSpace.halfBar
    const x2 = xAxis.convertToPixel(endIdx) + barSpace.halfBar
    ctx.fillStyle = color
    ctx.fillRect(x1, 0, x2 - x1, bounding.height)
  }

  for (let i = from; i < to; i++) {
    const data = kLineDataList[i]
    if (!data) continue

    const session = classifySessionSegment(data.timestamp, tz)
    let currentSession: 'pre' | 'after' | null = null

    if (session === 'pre') {
      currentSession = 'pre'
    } else if (session === 'after' || session === 'closed') {
      currentSession = 'after'
    }

    if (currentSession !== batchSession) {
      // Flush previous batch
      if (batchSession && batchStart >= 0) {
        const color = batchSession === 'pre' ? config.preMarketColor : config.afterHoursColor
        drawBatch(batchStart, i - 1, color)
      }
      batchStart = currentSession ? i : -1
      batchSession = currentSession
    }
  }

  // Flush final batch
  if (batchSession && batchStart >= 0) {
    const color = batchSession === 'pre' ? config.preMarketColor : config.afterHoursColor
    drawBatch(batchStart, to - 1, color)
  }

  ctx.restore()
  // false, not true: this shades the background and must not suppress the bars.
  return false
}

export const registerSessionHighlightIndicator = registerOnce(() => {
  registerIndicator({
    name: INDICATOR_NAME,
    shortName: '',
    figures: [],
    calc: (dataList: KLineData[]) => dataList.map(() => ({})),
    draw: (params: IndicatorDrawParams) =>
      drawSessionHighlight(params as unknown as SessionHighlightDrawParams),
  })
})

export const SESSION_HIGHLIGHT_NAME = INDICATOR_NAME
